import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Box,
  Stack,
  useComputedColorScheme,
} from "@mantine/core";
import { IconRocket, IconPlayerPlay } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import { ContactFormModal } from "./ContactFormModal";

const ease = [0.25, 0.46, 0.45, 0.94] as const;

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
};

// Slower stagger for the dashboard screenshot
const imageItem = {
  hidden: { opacity: 0, y: 40, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease, delay: 0.15 },
  },
};

export function HeroSection() {
  const [
    contactModalOpened,
    { open: openContactModal, close: closeContactModal },
  ] = useDisclosure(false);
  const computedColorScheme = useComputedColorScheme("light");

  // Scroll-linked parallax: blobs and screenshot drift at different rates
  // as the hero scrolls away, creating depth.
  const sectionRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const blobTopY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const blobBottomY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box
      component="section"
      ref={sectionRef}
      className="hero-bg-animate"
      style={{
        position: "relative",
        overflow: "hidden",
        background:
          computedColorScheme === "dark"
            ? "linear-gradient(135deg, #1A1B1E 0%, #2a3f35 25%, #25262B 50%, #1e3d30 75%, #1A1B1E 100%)"
            : "linear-gradient(135deg, #e8f4ff 0%, #ffffff 25%, #e6fcf5 50%, #f0f7f4 75%, #e8f4ff 100%)",
        minHeight: "calc(100vh - 70px)",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Decorative blobs — parallax-drift on scroll */}
      <motion.div
        style={{
          position: "absolute",
          top: "10%",
          right: "10%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(45, 156, 255, 0.1) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
          y: reduceMotion ? undefined : blobTopY,
        }}
      />
      <motion.div
        style={{
          position: "absolute",
          bottom: "20%",
          left: "5%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(18, 184, 134, 0.1) 0%, transparent 70%)",
          filter: "blur(80px)",
          pointerEvents: "none",
          y: reduceMotion ? undefined : blobBottomY,
        }}
      />

      <Container size="lg" style={{ position: "relative", zIndex: 1 }}>
        {/* Staggered entrance — fires on page load, not scroll */}
        <motion.div initial="hidden" animate="visible" variants={container}>
          <Stack align="center" gap="xl" py={80}>
            <motion.div variants={item}>
              <Text
                size="lg"
                fw={600}
                ta="center"
                c="healthyGreen"
                style={{ letterSpacing: "0.5px" }}
              >
                Compliance management for group homes
              </Text>
            </motion.div>

            <motion.div variants={item}>
              <Title
                order={1}
                fz={{ base: "2rem", xs: "2rem", sm: "2.5rem", md: "3.5rem" }}
                fw={700}
                ta="center"
                style={{ lineHeight: 1.2, maxWidth: "900px" }}
              >
                Pass Your State Survey{" "}
                <Text
                  component="span"
                  inherit
                  style={{
                    background:
                      "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Without the Fire Drill
                </Text>
              </Title>
            </motion.div>

            <motion.div variants={item}>
              <Text
                size="xl"
                c="dimmed"
                ta="center"
                maw={720}
                style={{ lineHeight: 1.6 }}
              >
                LivingSyncs encodes your state's regulations once, then enforces
                them automatically across every facility and every resident —
                and proves you were compliant on any day a surveyor asks. The
                complete group home platform, HIPAA-ready from day one.
              </Text>
            </motion.div>

            <motion.div variants={item}>
              {/* justify=center: below ~500px the two xl buttons no longer fit
                  side by side and wrap, and without this they stack flush left
                  while every other hero element is centred. */}
              <Group gap="md" mt="xl" justify="center">
                <Button
                  size="xl"
                  leftSection={<IconRocket size={20} />}
                  styles={{
                    root: {
                      background:
                        "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 25px rgba(74, 140, 113, 0.3)",
                      },
                    },
                  }}
                  onClick={openContactModal}
                >
                  Request a Demo
                </Button>
                <Button
                  size="xl"
                  variant="subtle"
                  leftSection={<IconPlayerPlay size={20} />}
                  onClick={() => scrollToSection("compliance-engine")}
                >
                  See the Lifecycle
                </Button>
              </Group>
            </motion.div>

            <motion.div variants={item}>
              <Text size="sm" c="dimmed" mt="xl">
                No setup fees · Personalized onboarding · HIPAA-ready
              </Text>
            </motion.div>

            {/* Dashboard screenshot — slightly slower entrance.
                Parallax lives on a nested wrapper: the entrance variant and
                the scroll-linked motion value would fight over `y` on the
                same element. */}
            <motion.div
              variants={imageItem}
              style={{ width: "100%", maxWidth: "1100px" }}
            >
              <motion.div style={{ y: reduceMotion ? undefined : imageY }}>
                <Box
                  mt={60}
                  style={{
                    borderRadius: "16px",
                    boxShadow:
                      computedColorScheme === "dark"
                        ? "0 20px 60px rgba(0, 0, 0, 0.5)"
                        : "0 20px 60px rgba(0, 0, 0, 0.15)",
                    border:
                      computedColorScheme === "dark"
                        ? "1px solid #373A40"
                        : "1px solid #e9ecef",
                    overflow: "hidden",
                    background:
                      computedColorScheme === "dark" ? "#1A1B1E" : "#fff",
                  }}
                >
                  {/* The full asset is a 3024px-wide desktop screenshot that
                      renders at ~13% scale on a phone — the UI text is
                      illegible at that size either way, so there is no reason
                      to ship 666 KB to get there. Uses <picture> with a media
                      query rather than srcset/sizes because density-based
                      selection would still pick the full asset: a 375px slot
                      at DPR 3 asks for 1125w. width/height carry the intrinsic
                      aspect ratio (identical in both assets) to avoid CLS. */}
                  <picture>
                    <source
                      media="(max-width: 768px)"
                      srcSet={
                        computedColorScheme === "dark"
                          ? "/assets/dashboard-preview-dark-900.png"
                          : "/assets/dashboard-preview-900.png"
                      }
                    />
                    <img
                      src={
                        computedColorScheme === "dark"
                          ? "/assets/dashboard-preview-dark.png"
                          : "/assets/dashboard-preview.png"
                      }
                      alt="LivingSyncs Dashboard - Resident Management"
                      width={3024}
                      height={1616}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </picture>
                </Box>
              </motion.div>
            </motion.div>
          </Stack>
        </motion.div>
      </Container>

      <ContactFormModal
        opened={contactModalOpened}
        onClose={closeContactModal}
      />
    </Box>
  );
}
